import { useCallback, useEffect, useRef, useState } from 'react';
import { MdCheckCircle, MdOutlineInfo } from 'react-icons/md';
import { FormInput, FormCheckbox, useToast } from '@podus/ui';
import { api, ApiError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { referralLinkFor } from '@/lib/referral';
import { useAuth } from '@/context/AuthContext';
import type { AmbassadorProfile } from '@/types';

interface AvailabilityResponse {
    available: boolean;
    code: string;
    message: string;
}

/** Profile, referral code and password — all in one place. */
export default function Settings() {
    const toast = useToast();
    const { user, login, token } = useAuth();

    const [profile, setProfile] = useState<AmbassadorProfile | null>(null);
    const [loading, setLoading] = useState(true);

    /* Profile form */
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [digestOptOut, setDigestOptOut] = useState(false);
    const [savingProfile, setSavingProfile] = useState(false);

    /* Code form */
    const [newCode, setNewCode] = useState('');
    const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);
    const [savingCode, setSavingCode] = useState(false);

    /* Password form */
    const [currentPassword, setCurrentPassword] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [savingPassword, setSavingPassword] = useState(false);

    const hydrate = useCallback((data: AmbassadorProfile) => {
        setProfile(data);
        setName(data.name ?? '');
        setPhone(data.phoneNumber ?? '');
        setDigestOptOut(data.weeklyDigestOptOut);
    }, []);

    useEffect(() => {
        api.get<{ data: AmbassadorProfile }>('/ambassador/me')
            .then((res) => hydrate(res.response.data))
            .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Could not load your settings'))
            .finally(() => setLoading(false));
    }, [hydrate, toast]);

    /* ── Live code availability (FR-20) ── */
    const debounceRef = useRef<number | undefined>(undefined);
    useEffect(() => () => window.clearTimeout(debounceRef.current), []);

    const onCodeChange = (value: string) => {
        const next = value.toLowerCase();
        setNewCode(next);
        window.clearTimeout(debounceRef.current);
        if (!next.trim()) {
            setAvailability(null);
            return;
        }
        debounceRef.current = window.setTimeout(() => {
            api.get<AvailabilityResponse>(`/ambassador/codes/availability?code=${encodeURIComponent(next.trim())}`)
                .then((res) => setAvailability(res.response))
                .catch(() => setAvailability(null));
        }, 350);
    };

    const saveProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        setSavingProfile(true);
        try {
            await api.patch('/ambassador/me', {
                name: name.trim(),
                phoneNumber: phone.replace(/\D/g, ''),
                weeklyDigestOptOut: digestOptOut,
            });
            toast.success('Profile updated');
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not save your profile');
        } finally {
            setSavingProfile(false);
        }
    };

    const saveCode = async (e: React.FormEvent) => {
        e.preventDefault();
        if (availability && !availability.available) return;

        setSavingCode(true);
        try {
            const res = await api.patch<{ data: { code: string; referralLink: string }; message: string }>(
                '/ambassador/me/code',
                { code: newCode.trim().toLowerCase() },
            );
            toast.success(res.response.message);

            // Keep the header/session in step with the new link straight away.
            if (user && token) {
                login(token, { ...user, code: res.response.data.code, referralLink: res.response.data.referralLink });
            }

            const refreshed = await api.get<{ data: AmbassadorProfile }>('/ambassador/me');
            hydrate(refreshed.response.data);
            setNewCode('');
            setAvailability(null);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not change your referral code');
        } finally {
            setSavingCode(false);
        }
    };

    const savePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== passwordConfirm) {
            toast.error('Passwords do not match');
            return;
        }

        setSavingPassword(true);
        try {
            const res = await api.post<{ token: string; message: string }>('/ambassador/me/change-password', {
                currentPassword,
                password,
                passwordConfirm,
            });
            if (user) login(res.response.token, user);
            toast.success(res.response.message);
            setCurrentPassword('');
            setPassword('');
            setPasswordConfirm('');
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not change your password');
        } finally {
            setSavingPassword(false);
        }
    };

    if (loading) {
        return (
            <div className="mx-auto max-w-3xl px-4 py-8 lg:px-8">
                <div className="skeleton-shimmer h-64 w-full rounded-xl" />
            </div>
        );
    }

    const changesLeft = profile ? Math.max(0, profile.codeChanges.lifetimeMax - profile.codeChanges.used) : 0;
    const codeLocked = changesLeft === 0;

    return (
        <div className="mx-auto max-w-3xl px-4 py-6 lg:px-8 lg:py-8">
            <h2 className="text-[22px] font-bold text-secondary">Settings</h2>

            {/* ── Profile ── */}
            <form onSubmit={saveProfile} className="mt-6 rounded-xl border border-[#E0E0E0] bg-white p-5 sm:p-6">
                <h3 className="text-[15px] font-bold text-secondary">Your profile</h3>
                <p className="mt-1 text-[13px] text-[#747474]">
                    Your email address is fixed to the one you were invited on.
                </p>

                <div className="mt-5 flex flex-col gap-4">
                    <FormInput
                        id="settings-name"
                        title="Full name"
                        variant="white"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <FormInput
                        id="settings-email"
                        title="Email"
                        variant="white"
                        value={profile?.email ?? ''}
                        disabled
                    />
                    <FormInput
                        id="settings-phone"
                        title="Phone number"
                        placeholder="2348012345678"
                        variant="white"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                    />

                    <FormCheckbox
                        id="settings-digest"
                        label="Don't send me the weekly performance digest"
                        checked={digestOptOut}
                        onChange={(e) => setDigestOptOut(e.target.checked)}
                    />
                </div>

                <button
                    type="submit"
                    disabled={savingProfile}
                    className="mt-6 rounded-lg bg-secondary px-5 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                >
                    {savingProfile ? 'Saving…' : 'Save profile'}
                </button>
            </form>

            {/* ── Referral code (§3.3) ── */}
            <form onSubmit={saveCode} className="mt-6 rounded-xl border border-[#E0E0E0] bg-white p-5 sm:p-6">
                <h3 className="text-[15px] font-bold text-secondary">Referral link</h3>
                <p className="mt-1 text-[13px] leading-5 text-[#747474]">
                    Your current link is{' '}
                    <span className="font-mono text-secondary">
                        {profile?.code ? referralLinkFor(profile.code) : '—'}
                    </span>
                </p>

                <div className="mt-4 flex items-start gap-2.5 rounded-lg bg-[#f8f8f8] px-4 py-3">
                    <MdOutlineInfo size={18} className="mt-0.5 shrink-0 text-[#9a9a9a]" />
                    <p className="text-[12px] leading-5 text-[#747474]">
                        Changing your code keeps the old one working forever — links you already shared will still be
                        credited to you. You have <strong>{changesLeft}</strong> change(s) left
                        {profile?.codeChanges.lastChangedAt
                            ? `, and last changed it on ${formatDate(profile.codeChanges.lastChangedAt)}.`
                            : '.'}
                    </p>
                </div>

                {profile && profile.aliases.length > 0 && (
                    <div className="mt-4">
                        <p className="text-[12px] font-semibold uppercase tracking-wide text-[#9a9a9a]">
                            Previous codes (still active)
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {profile.aliases.map((alias) => (
                                <span
                                    key={alias.code}
                                    className="rounded-full bg-[#f5f5f5] px-3 py-1 font-mono text-[12px] text-[#6b6b6b]"
                                >
                                    {alias.code}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-5">
                    <FormInput
                        id="settings-code"
                        title="New referral code"
                        placeholder="yourname"
                        variant="white"
                        disabled={codeLocked}
                        value={newCode}
                        onChange={(e) => onCodeChange(e.target.value)}
                        error={availability && !availability.available ? availability.message : undefined}
                    />
                    {availability?.available && (
                        <p className="mt-1 flex items-center gap-1 text-[11px] font-medium text-green-600">
                            <MdCheckCircle size={13} />
                            {availability.message}
                        </p>
                    )}
                </div>

                <button
                    type="submit"
                    disabled={savingCode || codeLocked || !newCode.trim() || availability?.available === false}
                    className="mt-5 rounded-lg bg-secondary px-5 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {savingCode ? 'Updating…' : codeLocked ? 'No changes remaining' : 'Update referral code'}
                </button>
            </form>

            {/* ── Password ── */}
            <form onSubmit={savePassword} className="mt-6 rounded-xl border border-[#E0E0E0] bg-white p-5 sm:p-6">
                <h3 className="text-[15px] font-bold text-secondary">Password</h3>
                <p className="mt-1 text-[13px] text-[#747474]">
                    Changing your password signs you out of every other device.
                </p>

                <div className="mt-5 flex flex-col gap-4">
                    <FormInput
                        id="password-current"
                        type="password"
                        title="Current password"
                        autoComplete="current-password"
                        variant="white"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                    <FormInput
                        id="password-new"
                        type="password"
                        title="New password"
                        autoComplete="new-password"
                        variant="white"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <FormInput
                        id="password-confirm"
                        type="password"
                        title="Confirm new password"
                        autoComplete="new-password"
                        variant="white"
                        value={passwordConfirm}
                        onChange={(e) => setPasswordConfirm(e.target.value)}
                        error={passwordConfirm && password !== passwordConfirm ? 'Passwords do not match' : undefined}
                    />
                </div>

                <button
                    type="submit"
                    disabled={savingPassword || !currentPassword || !password}
                    className="mt-6 rounded-lg bg-secondary px-5 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {savingPassword ? 'Updating…' : 'Change password'}
                </button>
            </form>
        </div>
    );
}
